import ButtonV2 from '@creator/ui/components/Button/ButtonV2';
import Text from '@creator/ui/components/Text/Text';
import { capitalizeFirstLetter } from '@creator/ui/utils/string';
import { cn } from '@creator/ui/utils/ui';
import Icon from '@src/basic-components/Icon/Icon';
import JoinButtonModal from '@src/components/JoinButtonModal/JoinButtonModal';
import ProfileImage from '@src/components/ProfileImage/ProfileImage';
import { ModalContext } from '@src/context/modal-context';
import type { UseModal } from '@src/hooks/use-modal';
import { ensureTokenUrl } from '@src/model/config/helpers';
import { useStoreState } from '@src/model/hooks';
import { getDisplayName, isUserJoinedToToken } from '@src/model/user/helpers';
import type { Post } from '@src/store/contentEntities/model';
import type { FC } from 'react';
import { memo, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

export interface CreateFeedItemInputProps {
    className?: string;
    tokenName: string;
    postKind: Post['kind'];
}

const CreateFeedItemInput: FC<CreateFeedItemInputProps> = props => {
    const { tokenName, className = '', postKind } = props;

    const { t } = useTranslation();
    const isLoggedIn = useStoreState(state => state.user.isLoggedIn);
    const navigate = useNavigate();
    const { handleModal } = useContext<UseModal>(ModalContext);

    function getBaseClassName(): string {
        return cn('flex items-center relative space-x-3', className);
    }

    const goToCreatePost = useCallback(() => {
        if (isLoggedIn && !isUserJoinedToToken(tokenName)) return handleModal(<JoinButtonModal tokenName={tokenName} />);
        if (!isLoggedIn) return navigate(`/bbs/${ensureTokenUrl(tokenName, false)}/posts/create/anonymous`);
        return navigate(`/bbs/${ensureTokenUrl(tokenName, false)}/posts/create?kind=${postKind}`);
    }, [isLoggedIn, tokenName]);

    function renderProfileImage() {
        return <ProfileImage tokenName={tokenName} className="w-8 h-8 lg:w-10 lg:h-10" displayName={getDisplayName() || ''} />;
    }

    function renderInput() {
        const _props = {
            onClick: goToCreatePost,
            className:
                'h-8 lg:h-10 group cursor-pointer flex items-center text-sm flex-1 rounded-lg px-4 transition border border-gray-200 dark:border-gray-600 hover:border-gray-500 dark:hover:border-gray-500',
        };

        return (
            <div {..._props}>
                <Text className="flex-1">{t(`tokenPage.createPostInput.create${capitalizeFirstLetter(postKind)}`)}</Text>
                {renderIcons()}
            </div>
        );
    }

    function renderIcons() {
        return <Icon svgFill={false} className="text-xl text-gray-500 transition group-hover:text-gray-900 dark:group-hover:text-gray-50" name="image" />;
    }

    function renderLeaderboardButton() {
        return (
            <Link to={`/${ensureTokenUrl(tokenName)}/tab/leaderboard`} className="">
                <ButtonV2 className="h-7 lg:h-9" size="md">
                    <Text>{t('tokenPage.createPostInput.leaderboard')}</Text>
                </ButtonV2>
            </Link>
        );
    }

    return (
        <div className={getBaseClassName()}>
            {renderProfileImage()}
            {renderInput()}
            {renderLeaderboardButton()}
        </div>
    );
};

export default memo(CreateFeedItemInput);
